/**
 * ClientLogger — structured client-side log capture, durable tiered buffer, and sync.
 *
 * - Captures: window.onerror + unhandledrejection, every network request
 *   (via observeRequest hook), explicit log() + breadcrumb() calls.
 * - Buffer: `clientlog.buffer` in PlatformIO.localStore (SQLite/IndexedDB).
 *   Level-tiered: error/warn retained until server-acked; info/debug ring-
 *   buffered to maxInfoEvents (default 5000). Drops are reported as a
 *   `lifecycle` event (never silent).
 * - Flush: batched (50), idempotent (client UUID), removes from buffer only
 *   after server ack. Gated on online + authenticated (mirrors TelemetryClient).
 * - Never throws into the app — all capture wrapped in try/catch.
 *
 * Client observability plan — Tasks 2, 3, 4.
 */

import { assertNoPixels, isSignedOut, redactEvent } from "@genz/platform";
import type { ClientLogEvent, ClientLogRow, LogCategory, LogLevel, TelemetryEvent } from "@genz/platform";
import type { LocalStore } from "@genz/platform";

export const CLIENTLOG_BUFFER = "clientlog.buffer";
/** Retained ring of recent error/warn events, kept ON-DEVICE and NOT drained on
 *  cloud flush — so the Error log can always show "what's happening with the app"
 *  irrespective of the cloud (offline, cloud down, or not-yet-synced). */
export const CLIENTLOG_LOCAL = "clientlog.local";
const DEFAULT_MAX_INFO = 5_000;
/** Cap on the error/warn tier of the durable buffer (MIN-7). The info/debug tier has had
 *  a cap since the start; error/warn had NONE, so a device that never reaches an
 *  authenticated flush (broken login, cloud down, offline laptop) grew this collection
 *  without bound in IndexedDB. 2 000 error/warn events is far more than any support
 *  investigation reads, and eviction is oldest-first so the freshest failure — the one
 *  being investigated — is the last thing to go. */
export const MAX_RETAINED_ERRORS = 2_000;
export const LOCAL_RING_MAX = 300;
const FLUSH_BATCH_SIZE = 50;
/** Periodic flush cadence. Connectivity events are the ONLY other flush trigger, and the
 *  desktop platform never fires them (`setOnline` is unwired there) — so without this timer
 *  a desktop session flushed exactly ONCE, at mount, usually before login: the cloud
 *  dashboard missed essentially everything ("it's not logging everything"). The timer also
 *  picks up the first post-login flush (flush() no-ops until authenticated). */
export const FLUSH_INTERVAL_MS = 30_000;

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------

export interface ClientLoggerOptions {
  store: LocalStore;
  /** Calls the existing `JsonClient.sendEvents` batch endpoint. */
  sendEvents: (events: TelemetryEvent[]) => Promise<{ accepted: number; rejected: number }>;
  platform: "web" | "desktop";
  appVersion: string;
  deviceId: string;
  sessionId: string;
  /** Max buffered info/debug events before oldest are dropped (default 5000). */
  maxInfoEvents?: number;
  /** Max buffered error/warn events before oldest are dropped (default 2000, MIN-7). */
  maxRetainedErrors?: number;
  isOnline: () => boolean;
  isAuthenticated: () => boolean;
  onConnectivityChange: (cb: (online: boolean) => void) => () => void;
}

// ---------------------------------------------------------------------------
// Buffer item shape
// ---------------------------------------------------------------------------

interface BufferItem {
  /** Same as the event id — used as the record key too. */
  bufferId: string;
  data: ClientLogEvent;
}

// ---------------------------------------------------------------------------
// ClientLogger
// ---------------------------------------------------------------------------

export class ClientLogger {
  private readonly opts: ClientLoggerOptions;
  private readonly store: LocalStore;
  private customerId?: string;
  private route?: string;
  private flushing = false;
  private unsub?: () => void;
  private unhandlers: (() => void)[] = [];
  private flushTimer?: ReturnType<typeof setInterval>;
  /**
   * F-10 — durable writes are OFF while the sign-out purge runs.
   *
   * Sign-out clears the very stores this logger writes into. A write that lands mid-purge
   * rejects; the rejection reaches `window.onunhandledrejection`, which this logger handles by
   * CAPTURING it — another write, into the same store that is being cleared. That is how a
   * sign-out ended with a console full of IndexedDB errors and a log nobody could read.
   *
   * While suspended, `capture()` still builds, redacts and echoes the event (so nothing on the
   * way out is invisible) but writes nothing durable, and `flush()` no-ops. `purgeBuffers()` —
   * the FIRST logger-owned step of every sign-out path — turns it on, and the session-gained
   * edge turns it back off.
   */
  private durableWritesSuspended = false;
  /**
   * F-4 — what the suspended window keeps INSTEAD of a durable write.
   *
   * The window said, in three comments, that events were "still built, redacted and echoed".
   * They were not: `capture()` returned before both sinks, so every failure raised while the
   * sign-out purge ran — exactly the window where a sign-out goes wrong — vanished with no
   * trace at all. These two fields are the trace: a capped in-memory ring that
   * `recentLocalErrors()` merges, and a count of what could not be persisted, reported once
   * when the window closes.
   */
  private suspendedRing: ClientLogEvent[] = [];
  private suspendedDropped = 0;
  /**
   * F-11 — durable writes this capture has started but not finished.
   *
   * `capture()` is synchronous and fires `_bufferEvent`/`_retainLocal` fire-and-forget. A write
   * already in flight when `purgeBuffers()` starts settles AFTER the purge has read and removed
   * rows, so an event captured under account A survives into account B's session and flushes
   * under B's token — the exact attribution bug the purge exists to prevent. The purge drains
   * this set first.
   */
  private pendingWrites = new Set<Promise<void>>();
  /** Approximate in-memory count of info/debug events in the buffer. Kept in sync
   *  to avoid a full `all()` query on every capture. Reset on flush. */
  private infoDebugCount = 0;
  /** Same, for the error/warn tier (MIN-7). Seeded in `start()` and recalibrated after
   *  every flush, exactly like `infoDebugCount`. */
  private errorWarnCount = 0;
  /** Drop-report coalescing: cap-evictions since the last report, and whether a report
   *  event is already buffered. One summary per flush cycle instead of one warn PER drop
   *  (the old 1:1 amplifier permanently filled the Error view once the cap was hit).
   *
   *  Tallied PER TIER: one shared counter reported under an "info/debug" label, so once
   *  MIN-7's error/warn cap fired an operator was told the wrong tier had been shed —
   *  and shed errors mean something very different from shed breadcrumbs (review-1 M-4).
   *  Coalescing still uses the single `dropReported` flag: one report per flush cycle. */
  private droppedInfoDebug = 0;
  private droppedErrorWarn = 0;
  private dropReported = false;

  constructor(opts: ClientLoggerOptions) {
    this.opts = opts;
    this.store = opts.store;
  }

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  /** Set the current customer ID (called after login). */
  setCustomer(id: string): void {
    this.customerId = id;
  }

  /** Update the current UI route (called on navigation). */
  setRoute(route: string): void {
    this.route = route;
  }

  /**
   * Capture a structured log event — sync, never throws.
   * Accepts a partial event; missing fields (id, occurredAt, context static fields) are filled.
   */
  capture(partial: {
    level: LogLevel;
    category: LogCategory;
    message: string;
    stack?: string;
    context?: Partial<ClientLogEvent["context"]>;
  }): void {
    // Wrap everything in try/catch so a bug here can never break a feature.
    try {
      const event: ClientLogEvent = {
        id: crypto.randomUUID(),
        occurredAt: Date.now(),
        level: partial.level,
        category: partial.category,
        message: partial.message,
        stack: partial.stack,
        context: {
          appVersion: this.opts.appVersion,
          platform: this.opts.platform,
          deviceId: this.opts.deviceId,
          sessionId: this.opts.sessionId,
          customerId: this.customerId,
          route: this.route,
          ...partial.context,
        },
      };

      // Redact + pixel-guard. An event that still trips the guard after redaction must not
      // persist — but a SILENT drop hid entire failure classes (a secret-store or provider
      // error message carries exactly the words the guard scans for). Buffer a sanitized
      // stub instead, so the drop itself is visible in the log.
      const redacted = redactEvent(event);
      try {
        assertNoPixels(redacted);
      } catch {
        const stub: ClientLogEvent = {
          id: event.id,
          occurredAt: event.occurredAt,
          level: event.level,
          category: event.category,
          // Deliberately generic wording — must not contain any guard-triggering term.
          message: `[guarded] a ${event.level} log entry was withheld by the content guard (its detail matched sensitive-content patterns)`,
          context: {
            appVersion: this.opts.appVersion,
            platform: this.opts.platform,
            deviceId: this.opts.deviceId,
            sessionId: this.opts.sessionId,
            customerId: this.customerId,
            route: this.route,
          },
        };
        if (this.durableWritesSuspended) {
          this._echoSuspended(stub);
        } else {
          this._track(this._bufferEvent(stub));
          if (stub.level === "error" || stub.level === "warn") this._track(this._retainLocal(stub));
        }
        return;
      }

      // F-10/F-4: nothing DURABLE while the sign-out purge is clearing these stores — but the
      // event is still echoed to the console, kept in a capped in-memory ring the Error drawer
      // reads, and counted. A window that swallows is the defect this wave removes, not a
      // smaller version of it.
      if (this.durableWritesSuspended) {
        this._echoSuspended(redacted);
        return;
      }
      // Buffer asynchronously (don't await in the sync capture path).
      // Catch async errors so a store failure never surfaces as an unhandled rejection.
      this._track(this._bufferEvent(redacted));
      // Retain errors/warns AND user/step breadcrumbs (action/lifecycle) in a device-
      // local ring so BOTH the Error log and the live Activity trail show up on-device
      // irrespective of the cloud (offline, cloud down, not-yet-synced). Network-level
      // info (every API call) is intentionally NOT retained locally — it would flood the
      // ring and evict the events that matter; those still flush to the cloud dashboard.
      if (
        redacted.level === "error" ||
        redacted.level === "warn" ||
        redacted.category === "action" ||
        redacted.category === "lifecycle"
      ) {
        this._track(this._retainLocal(redacted));
      }
    } catch {
      // Swallow all errors — the logger must never throw into the app.
    }
  }

  /** Retain an error/warn event in the device-local ring (capped, never drained on
   *  flush). Best-effort: a store failure never breaks capture. */
  private async _retainLocal(event: ClientLogEvent): Promise<void> {
    await this.store.records.put(CLIENTLOG_LOCAL, event.id, event);
    const all = await this.store.records.all<ClientLogEvent>(CLIENTLOG_LOCAL);
    if (all.length > LOCAL_RING_MAX) {
      all.sort((a, b) => a.occurredAt - b.occurredAt);
      for (const old of all.slice(0, all.length - LOCAL_RING_MAX)) {
        await this.store.records.remove(CLIENTLOG_LOCAL, old.id);
      }
    }
  }

  /** Recent device-local error/warn events as `ClientLogRow`s (newest first) — the
   *  Error log merges these so app failures show even with NO cloud. */
  async recentLocalErrors(limit = LOCAL_RING_MAX): Promise<ClientLogRow[]> {
    const stored = await this.store.records.all<ClientLogEvent>(CLIENTLOG_LOCAL).catch(() => []);
    // F-4: the suspended window's in-memory ring is merged in, so a failure raised DURING a
    // sign-out is readable in the Error drawer even though nothing was persisted for it.
    const all = this.suspendedRing.length > 0 ? [...stored, ...this.suspendedRing] : stored;
    return all
      .sort((a, b) => b.occurredAt - a.occurredAt)
      .slice(0, limit)
      .map((e) => ({
        id: e.id,
        level: e.level,
        category: e.category,
        message: e.message,
        stack: e.stack ?? null,
        context: (e.context ?? {}) as Record<string, unknown>,
        app_version: e.context?.appVersion ?? "",
        platform: e.context?.platform ?? "",
        device_id: e.context?.deviceId ?? "",
        session_id: e.context?.sessionId ?? "",
        customer_id: e.context?.customerId ?? null,
        occurred_at: new Date(e.occurredAt).toISOString(),
      }));
  }

  /**
   * Normalize a call-site context bag into `{ stack, detail }`:
   *  - an `error` key that is an `Error` yields the stack trace + a serialized
   *    `{ name, message }` under `detail.error` (Error objects don't JSON-serialize);
   *  - an explicit string `stack` is honored;
   *  - every other key is carried through as structured `detail`.
   * This is what turns a bare message into an actionable "trace + details" entry.
   */
  private normalizeCtx(ctx?: Record<string, unknown>): {
    stack?: string;
    detail?: Record<string, unknown>;
  } {
    if (!ctx) return {};
    const { error, stack: rawStack, ...rest } = ctx;
    let stack = typeof rawStack === "string" ? rawStack : undefined;
    const detail: Record<string, unknown> = { ...rest };
    if (error instanceof Error) {
      stack = stack ?? error.stack;
      detail.error = { name: error.name, message: error.message };
    } else if (error !== undefined) {
      detail.error = error;
    }
    return { stack, detail: Object.keys(detail).length > 0 ? detail : undefined };
  }

  /**
   * Explicit log call. `ctx` carries structured detail that is PERSISTED (previously
   * dropped): pass `{ error }` for a stack trace, plus any `{ step, urlHost, status, … }`
   * you want in the entry. `logger.log("error", "Sync PUT failed", { error, step, status })`.
   */
  log(level: LogLevel, message: string, ctx?: Record<string, unknown>): void {
    const { stack, detail } = this.normalizeCtx(ctx);
    this.capture({
      level,
      category: level === "error" || level === "warn" ? "error" : "action",
      message,
      stack,
      context: detail ? { detail } : undefined,
    });
  }

  /** Record a breadcrumb (what the user just did — the live "what led to it" trail).
   *  `data` is persisted as structured `detail` (previously dropped). */
  breadcrumb(category: LogCategory, message: string, data?: Record<string, unknown>): void {
    const { stack, detail } = this.normalizeCtx(data);
    this.capture({ level: "info", category, message, stack, context: detail ? { detail } : undefined });
  }

  /** Called by the trackedFetch hook after every request completes or rejects. */
  observeRequest(req: {
    method: string;
    urlPath: string;
    status?: number;
    durationMs?: number;
    ok: boolean;
    error?: string;
    requestId?: string;
    errorBody?: unknown;
  }): void {
    // Severity by STATUS CLASS, not by `ok`. Every expected 4xx — the pre-refresh 401,
    // a non-admin 403, an unconfigured 404, an optimistic-concurrency 409 — used to be
    // logged as `error`, flooding the Error tab and evicting real errors from the local
    // ring ("lots of errors" report). error = 5xx/network-level failure only;
    // warn = throttle/timeout answers; info = the rest (still flushed to the dashboard).
    const status = req.status ?? 0;
    const level: LogLevel = req.ok
      ? "info"
      : !status || status >= 500
        ? "error"
        : status === 429 || status === 408
          ? "warn"
          : "info";
    // The server's own words belong in the MESSAGE where the list view shows them — the
    // parsed body's `detail` (FastAPI's convention) beats a bare status code. The full body
    // goes into context.detail for the drawer. This is what used to be thrown away: the
    // observation fired before the body was read, so every drawer under a failed request
    // was empty.
    const bodyDetail =
      req.errorBody && typeof req.errorBody === "object" && "detail" in req.errorBody
        ? String((req.errorBody as { detail: unknown }).detail)
        : undefined;
    const message = req.ok
      ? `${req.method} ${req.urlPath} → ${req.status ?? "?"} (${req.durationMs ?? "?"}ms)`
      : `${req.method} ${req.urlPath} → ${req.status ?? "network failure"}${
          bodyDetail ? `: ${bodyDetail}` : req.error ? `: ${req.error}` : ""
        }`;
    this.capture({
      level,
      category: "network",
      message,
      context: {
        // Top-level too (not only inside `request`): the dashboard's request_id filter and
        // the cloud's own server-error rows both read context.requestId at the same path.
        ...(req.requestId ? { requestId: req.requestId } : {}),
        request: {
          method: req.method,
          urlPath: req.urlPath,
          status: req.status,
          durationMs: req.durationMs,
          ok: req.ok,
          ...(req.requestId ? { requestId: req.requestId } : {}),
        },
        ...(req.errorBody !== undefined ? { detail: { errorBody: req.errorBody } } : {}),
      },
    });
  }

  /**
   * Install global error handlers (window.onerror + unhandledrejection).
   * Idempotent — calling multiple times is safe (handlers are stored and can be
   * removed via stop()).
   */
  installGlobalHandlers(): void {
    if (typeof window === "undefined") return;

    const onError = (event: ErrorEvent) => {
      try {
        const err = event.error as Error | undefined;
        this.capture({
          level: "error",
          category: "error",
          message: event.message || (err?.message ?? "Unknown error"),
          stack: err?.stack,
          // Where it happened — these events used to carry NO context at all, which is one
          // of the ways an opened error drawer ended up empty.
          context: {
            detail: {
              route: window.location?.pathname ?? null,
              ...(event.filename
                ? { source: `${event.filename.split("/").pop()}:${event.lineno ?? 0}` }
                : {}),
            },
          },
        });
      } catch {
        // never rethrow
      }
    };

    const onRejection = (event: PromiseRejectionEvent) => {
      if (isSignedOut(event.reason)) return; // a request the sign-out cancelled is not an error
      try {
        const reason = event.reason as Error | string | undefined;
        const message =
          reason instanceof Error
            ? reason.message
            : typeof reason === "string"
              ? reason
              : "Unhandled promise rejection";
        const stack = reason instanceof Error ? reason.stack : undefined;
        this.capture({
          level: "error",
          category: "error",
          message,
          stack,
          context: { detail: { route: window.location?.pathname ?? null } },
        });
      } catch {
        // never rethrow
      }
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);

    // EVERY CLICK on an interactive control leaves an `action` breadcrumb ("every click
    // should be counted"). Text only — the control's accessible label, truncated; never
    // input values, so nothing sensitive can ride along. Clicks are user-paced, so the
    // volume is trivial next to the (now-silenced) heartbeat noise.
    const onClick = (event: MouseEvent) => {
      try {
        const target = event.target as HTMLElement | null;
        const el = target?.closest?.("button, a, [role='button'], [role='tab'], summary");
        if (!el) return;
        const label =
          (el.getAttribute("aria-label") || el.getAttribute("data-testid") || el.textContent || "")
            .trim().replace(/\s+/g, " ").slice(0, 60) || el.tagName.toLowerCase();
        this.capture({ level: "info", category: "action", message: `Click · ${label}` });
      } catch {
        // never rethrow
      }
    };
    document.addEventListener("click", onClick, true); // capture phase — fires even when handlers stopPropagation

    this.unhandlers.push(
      () => window.removeEventListener("error", onError),
      () => window.removeEventListener("unhandledrejection", onRejection),
      () => document.removeEventListener("click", onClick, true),
    );
  }

  /** Start connectivity-driven + periodic flush. Also drains any buffer from previous
   *  sessions. The interval is the desktop's ONLY recurring flush driver (no connectivity
   *  events fire there) and delivers the first post-login flush everywhere. */
  start(): void {
    this.unsub = this.opts.onConnectivityChange((online) => {
      if (online) void this.flush();
    });
    this.flushTimer = setInterval(() => { void this.flush(); }, FLUSH_INTERVAL_MS);
    // Seed the info/debug count from the durable buffer before the first capture,
    // so an offline restart with a full buffer doesn't temporarily double the cap.
    void this._recalibrateCounts().then(() => {
      // Drain from a previous session immediately (after seeding the counts).
      void this.flush();
    }).catch(() => {
      // If the read fails (e.g. store not yet ready), just flush — the count will
      // be recalibrated after the next successful flush anyway.
      void this.flush();
    });
  }

  /**
   * F-10 — stop writing durably, now, and stop the flush timer with it.
   *
   * Called by `purgeBuffers()`, which every sign-out path runs BEFORE the tiers that clear
   * the content stores. It is deliberately not `stop()`: the global error/click handlers stay
   * installed, so a failure DURING the sign-out is still captured and echoed — it just is not
   * written into a store that is being emptied underneath it.
   */
  /**
   * F-4 — the suspended window's sink. Console (so devtools and the terminal see it), a capped
   * in-memory ring (so the Error drawer still shows it), and a counter (so the operator learns
   * how much went unwritten). No store is touched.
   */
  /** F-11 — register a fire-and-forget durable write so `purgeBuffers()` can wait for it. A
   *  rejection is swallowed exactly as it was before (a store failure must never surface as an
   *  unhandled rejection); only the WAITING is new. */
  private _track(p: Promise<void>): void {
    const tracked = p.catch(() => {}).finally(() => { this.pendingWrites.delete(tracked); });
    this.pendingWrites.add(tracked);
  }

  private _echoSuspended(event: ClientLogEvent): void {
    this.suspendedDropped += 1;
    this.suspendedRing.push(event);
    if (this.suspendedRing.length > LOCAL_RING_MAX) {
      this.suspendedRing.splice(0, this.suspendedRing.length - LOCAL_RING_MAX);
    }
    try {
      const line = `[clientlog·suspended] ${event.level} ${event.message}`;
      if (event.level === "error" || event.level === "warn") console.error(line);
      else console.info(line);
    } catch { /* a broken console must not break a capture */ }
  }

  /** F-4 — events the suspended window could not persist. Reset when the window closes. */
  get suspendedDropCount(): number {
    return this.suspendedDropped;
  }

  suspendDurableWrites(): void {
    this.durableWritesSuspended = true;
    if (this.flushTimer !== undefined) { clearInterval(this.flushTimer); this.flushTimer = undefined; }
  }

  /** F-10 — durable logging resumes once a session exists again (bootstrap's session edge).
   *  Idempotent: a second call while already running must not start a second timer. */
  resumeDurableWrites(): void {
    if (!this.durableWritesSuspended) return;
    this.durableWritesSuspended = false;
    // F-4: say how much the window ate, ONCE, on the way out. The logger cannot log this to
    // itself without re-entering the path it is reporting on, so it goes to the console.
    if (this.suspendedDropped > 0) {
      try {
        console.warn(
          `[clientlog] ${this.suspendedDropped} event(s) were captured while durable logging was ` +
          "suspended for sign-out; they were echoed to the console and the on-device Error log " +
          "but not persisted or sent.",
        );
      } catch { /* a broken console must not break the resume */ }
    }
    this.suspendedDropped = 0;
    this.suspendedRing = [];
    if (this.flushTimer === undefined) {
      this.flushTimer = setInterval(() => { void this.flush(); }, FLUSH_INTERVAL_MS);
    }
  }

  /** F-10 — whether durable writes are currently withheld. Read by the sign-out test. */
  get durableWritesOff(): boolean {
    return this.durableWritesSuspended;
  }

  /** Stop flush subscription, the periodic flush timer, and global handlers. */
  stop(): void {
    this.unsub?.();
    if (this.flushTimer !== undefined) { clearInterval(this.flushTimer); this.flushTimer = undefined; }
    for (const fn of this.unhandlers) fn();
    this.unhandlers = [];
  }

  /** Flush buffered events in batches of 50. No-op when offline or unauthenticated.
   *  Events are removed from the buffer ONLY after the server acks. On send failure,
   *  the buffer is kept intact for the next retry. Mirrors TelemetryClient.flush. */
  async flush(): Promise<void> {
    // F-10: a flush reads AND removes rows. Not while sign-out is clearing the same store.
    if (this.durableWritesSuspended) return;
    // `isOnline()` is a HINT, never a gate.
    //
    // It comes from `navigator.onLine`, which inside a Tauri WebView is unreliable: it can
    // report offline while the machine is fine, and the `online` event does not always fire on
    // reconnect. Gating on it meant one wrong value silenced log delivery FOREVER — the periodic
    // flush no-op'd too, so nothing self-healed and the cloud Activity log simply stayed empty.
    // The network call is the only authority on reachability, and a failed attempt is already
    // handled: the buffer is kept and retried. One doomed request per 30s while genuinely
    // offline is a trivial price for never silently losing the log.
    //
    // Authentication IS still a gate — without a token the POST is a guaranteed 401, and those
    // events must wait for login rather than burn retries.
    if (this.flushing || !this.opts.isAuthenticated()) return;
    this.flushing = true;
    try {
      let buffered = await this.store.records.all<BufferItem>(CLIENTLOG_BUFFER);
      // Same reasoning inside the loop: a mid-drain hint flip must not abandon the drain. A
      // real failure breaks out below and keeps the remaining buffer.
      while (buffered.length > 0) {
        const slice = buffered.slice(0, FLUSH_BATCH_SIZE);
        const events: TelemetryEvent[] = slice.map((item) => ({
          type: "clog" as const,
          clientTs: item.data.occurredAt,
          data: item.data,
        }));
        try {
          const res = await this.opts.sendEvents(events);
          // The server counts rejects (wire-shape/guard failures) — they used to be deleted
          // client-side as if acked, with zero trace anywhere a user can see. Record them.
          if (res && typeof res.rejected === "number" && res.rejected > 0) {
            this.capture({
              level: "warn", category: "lifecycle",
              message: `ingestion rejected ${res.rejected} of ${events.length} log event(s) — they will not appear in the cloud dashboard`,
            });
          }
        } catch (e) {
          // A TRANSIENT failure (network, 5xx, 401/403/408/429) keeps the buffer for the next
          // retry. A PERMANENT rejection (413/422/…) used to keep it too — a single poison
          // batch then stalled the WHOLE queue forever (same head-of-buffer 50 re-sent on
          // every flush, error/warn never ring-dropped, buffer only grows). Dead-letter it.
          const status = (e as { status?: unknown })?.status;
          const permanent = typeof status === "number" && status >= 400 && status < 500
            && status !== 401 && status !== 403 && status !== 408 && status !== 429;
          if (!permanent) break; // keep buffer intact; retry on next timer/reconnect
          for (const item of slice) {
            await this.store.records.remove(CLIENTLOG_BUFFER, item.bufferId);
          }
          this.capture({
            level: "warn", category: "lifecycle",
            message: `dropped a batch of ${slice.length} buffered log event(s) the server permanently rejected (HTTP ${status}) — the flush queue was stalled behind it`,
          });
          buffered = await this.store.records.all<BufferItem>(CLIENTLOG_BUFFER);
          continue;
        }
        // Remove from buffer only after ack
        for (const item of slice) {
          await this.store.records.remove(CLIENTLOG_BUFFER, item.bufferId);
        }
        buffered = await this.store.records.all<BufferItem>(CLIENTLOG_BUFFER);
      }
      // A completed flush cycle re-arms the (coalesced) drop report and folds the tally in.
      if (this.dropReported && (this.droppedInfoDebug > 0 || this.droppedErrorWarn > 0)) {
        const info = this.droppedInfoDebug;
        const errs = this.droppedErrorWarn;
        this.droppedInfoDebug = 0;
        this.droppedErrorWarn = 0;
        this.dropReported = false;
        this.breadcrumb(
          "lifecycle",
          `log cap evictions since the last report: ${info} info/debug and ${errs} error/warn log(s) dropped`,
        );
      }
      // Recalibrate our in-memory tier counters after flush.
      await this._recalibrateCounts();
    } finally {
      this.flushing = false;
    }
  }

  /** Sign-out: drop everything ATTRIBUTED to the account that is leaving, keep what was
   *  never attributed to anyone.
   *
   *  The blanket clear had a bad interaction with `flush()`'s (correct) "no token, no
   *  POST" gate: a session that never authenticated — a broken login, an activation 500,
   *  DNS down — captured every detail and then deleted it on the way out, so the cloud
   *  dashboard showed silence for exactly the failures that matter most (AUD-4 IMP-5).
   *  An event with no `customerId` was captured before anyone signed in, so keeping it
   *  raises no cross-account attribution question — that was the reason for the purge. */
  async purgeBuffers(): Promise<void> {
    // F-10 — FIRST, before a single row is read or removed. Sign-out clears the content
    // stores right after this, and a capture landing in between rejects into the very
    // handler that would capture it again.
    this.suspendDurableWrites();
    // F-11 — then DRAIN what is already in flight. `capture()` is synchronous and its writes
    // are fire-and-forget, so without this a write started microseconds before the suspend
    // lands after the purge has finished — and account A's event flushes under account B's
    // token. Suspending stops new writes; this stops the ones already running.
    while (this.pendingWrites.size > 0) {
      await Promise.all([...this.pendingWrites]);
    }
    try {
      const buffered = await this.store.records.all<BufferItem>(CLIENTLOG_BUFFER);
      for (const item of buffered) {
        if (item.data.context?.customerId) {
          await this.store.records.remove(CLIENTLOG_BUFFER, item.bufferId);
        }
      }
    } catch (e) {
      // The selective purge is an `all()` plus one `remove()` per row, so a failure can
      // land MID-LOOP and leave events attributed to the account that is leaving — which
      // the NEXT account's token would flush (review-1 M-5). Fall back to the blanket
      // clear: losing un-attributed pre-login events is the lesser harm, and it is what
      // sign-out did before IMP-5. The logger cannot log this to itself (its own store is
      // the thing that failed), so it goes to the console rather than nowhere.
      console.warn("[clientlog] selective sign-out purge failed; attempting to clear the whole buffer", e);
      await this.store.records.clear(CLIENTLOG_BUFFER).catch((e2) =>
        console.warn("[clientlog] fallback clear failed too; attributed events may remain", e2),
      );
    }
    await this.store.records.clear(CLIENTLOG_LOCAL).catch(() => {});
    await this._recalibrateCounts();
  }

  /** Count of un-synced events in the buffer. */
  async pendingCount(): Promise<number> {
    return (await this.store.records.all(CLIENTLOG_BUFFER)).length;
  }

  /** Events captured but not yet accepted by the cloud — surfaced in Observability so
   *  "nothing was logged" and "nothing was DELIVERED" stop looking identical. */
  async undeliveredCount(): Promise<number> {
    return this.pendingCount();
  }

  /** Flush right now.
   *
   *  `flush()` no-ops until `isAuthenticated()`, and the only other trigger is the 30 s
   *  `FLUSH_INTERVAL_MS` timer, so everything captured before login sat for up to half a
   *  minute after a session finally appeared — and a login-time failure is the single
   *  event most worth having in the cloud (AUD-4 IMP-5). `bootstrap.ts` calls this on the
   *  session-gained edge. */
  async flushNow(): Promise<void> {
    return this.flush();
  }

  /** Re-derive both tier counters from the durable buffer. */
  private async _recalibrateCounts(): Promise<void> {
    const remaining = await this.store.records
      .all<BufferItem>(CLIENTLOG_BUFFER)
      .catch(() => [] as BufferItem[]);
    this.infoDebugCount = remaining.filter(
      (b) => b.data.level === "info" || b.data.level === "debug",
    ).length;
    this.errorWarnCount = remaining.filter(
      (b) => b.data.level === "error" || b.data.level === "warn",
    ).length;
  }

  // -------------------------------------------------------------------------
  // Private helpers
  // -------------------------------------------------------------------------

  /** Persist an event to the durable buffer, enforcing the info/debug and error/warn caps. */
  private async _bufferEvent(event: ClientLogEvent): Promise<void> {
    const isInfoDebug = event.level === "info" || event.level === "debug";
    const isErrorWarn = event.level === "error" || event.level === "warn";
    const maxInfo = this.opts.maxInfoEvents ?? DEFAULT_MAX_INFO;
    const maxErrors = this.opts.maxRetainedErrors ?? MAX_RETAINED_ERRORS;

    // MIN-7: the error/warn tier had no cap at all. Same eviction shape as info/debug —
    // oldest-first, coalesced into the SAME one-report-per-flush-cycle mechanism rather
    // than a second one, so a capped device still reports one summary instead of one warn
    // per drop (the amplifier that fix already avoided once). The TALLY is per tier
    // (review-1 M-4): a shed error is not a shed breadcrumb.
    if (isErrorWarn && this.errorWarnCount >= maxErrors) {
      const allItems = await this.store.records.all<BufferItem>(CLIENTLOG_BUFFER);
      const errItems = allItems.filter(
        (b) => b.data.level === "error" || b.data.level === "warn",
      );
      errItems.sort((a, b) => a.data.occurredAt - b.data.occurredAt);
      const dropped = errItems.slice(0, Math.max(1, errItems.length - maxErrors + 1));
      for (const d of dropped) {
        await this.store.records.remove(CLIENTLOG_BUFFER, d.bufferId);
      }
      this.errorWarnCount -= dropped.length;
      this.droppedErrorWarn += dropped.length;
      await this._emitDropReport(
        `error/warn log cap (${maxErrors}) reached — oldest entries dropped until the `
        + "next successful flush (count rides this entry)",
      );
    }

    if (isInfoDebug && this.infoDebugCount >= maxInfo) {
      // Ring-buffer: remove the oldest info/debug event.
      const allItems = await this.store.records.all<BufferItem>(CLIENTLOG_BUFFER);
      const infoItems = allItems.filter(
        (b) => b.data.level === "info" || b.data.level === "debug",
      );
      // Sort by occurredAt ascending (oldest first) — id ordering is insertion-ordered.
      infoItems.sort((a, b) => a.data.occurredAt - b.data.occurredAt);

      const dropped = infoItems.slice(0, 1);
      for (const d of dropped) {
        await this.store.records.remove(CLIENTLOG_BUFFER, d.bufferId);
      }
      this.infoDebugCount--;
      this.droppedInfoDebug += dropped.length;

      // Record the drops as ONE coalesced lifecycle event per flush cycle (never silent,
      // never 1:1). The old per-drop `warn` became a self-amplifier: once the cap was hit,
      // EVERY info capture produced a warn that filled the Error view with logs about logs.
      // Lifecycle events are warn — they don't count against the info/debug cap.
      await this._emitDropReport(
        `info/debug log cap (${maxInfo}) reached — dropping oldest entries until the next successful flush (count rides this entry)`,
      );
    }

    const item: BufferItem = { bufferId: event.id, data: event };
    await this.store.records.put(CLIENTLOG_BUFFER, event.id, item);

    if (isInfoDebug) {
      this.infoDebugCount++;
    } else if (isErrorWarn) {
      this.errorWarnCount++;
    }
  }

  /** Buffer ONE coalesced cap-eviction report (a warn) until the next flush clears the
   *  flag. Written straight to the store rather than through `capture()`, so reporting a
   *  drop can never itself trigger another eviction. It is also retained in the local ring
   *  because it is a warn, and the ring is where the on-device Error log reads from —
   *  "the buffer is shedding your oldest failures" is exactly the kind of thing that must
   *  not be invisible while the cloud is unreachable. */
  private async _emitDropReport(message: string): Promise<void> {
    if (this.dropReported) return;
    this.dropReported = true;
    const dropEvent: ClientLogEvent = {
      id: crypto.randomUUID(),
      occurredAt: Date.now(),
      level: "warn",
      category: "lifecycle",
      message,
      context: {
        appVersion: this.opts.appVersion,
        platform: this.opts.platform,
        deviceId: this.opts.deviceId,
        sessionId: this.opts.sessionId,
        customerId: this.customerId,
      },
    };
    await this.store.records.put(CLIENTLOG_BUFFER, dropEvent.id, {
      bufferId: dropEvent.id, data: dropEvent,
    } satisfies BufferItem);
    this.errorWarnCount++;
    await this._retainLocal(dropEvent).catch(() => {});
  }
}
